import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckCircle, AlertCircle, Clock, Info } from 'lucide-react';
import { toast } from 'react-toastify';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export default function SettlementSync({ storeId }) {
  const [syncing, setSyncing] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res  = await fetch(`${API_BASE_URL}/amazon/${storeId}/settlement/status`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) setReports(data.reports || []);
    } catch (e) { console.error('Settlement status error', e); }
    finally { setLoading(false); }
  }, [storeId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      toast.info('Fetching settlement reports from Amazon (this may take a few minutes)...');
      const res  = await fetch(`${API_BASE_URL}/amazon/${storeId}/settlement/sync`, {
        method: 'POST', headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        toast.success(
          `✅ Settlement sync complete! ${data.reports_processed} reports processed, ${data.orders_updated} orders updated with actual fees.`
        );
        fetchStatus();
      } else {
        toast.error(data.error || 'Settlement sync failed');
      }
    } catch { toast.error('Settlement sync failed'); }
    finally { setSyncing(false); }
  };

  const lastSuccessReport = reports.find(r => r.status === 'success');

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            Settlement Report Sync
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
              Actual Fees
            </span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Fetches all available Amazon settlement reports (up to 18 months) and updates orders 
            with <strong>actual fees</strong> — referral, FBA, and other fees — for accurate profit calculation.
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 font-medium text-sm whitespace-nowrap"
        >
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing...' : 'Sync All Settlement Reports'}
        </button>
      </div>

      {/* Info box */}
      <div className="mt-4 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
        <Info size={15} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-700">
          Amazon generates settlement reports every 2 weeks. This sync fetches all reports from the last 18 months
          and replaces estimated fees with actual fees for each order. Orders not covered by any settlement report
          will keep estimated fees.
        </p>
      </div>

      {/* Last sync status */}
      {!loading && (
        <div className="mt-4">
          {lastSuccessReport ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1.5 text-sm text-green-600">
                <CheckCircle size={15} />
                Last synced: {new Date(lastSuccessReport.created_at).toLocaleString()}
              </span>
              <span className="text-sm text-gray-400">—</span>
              <span className="text-sm text-gray-600">{lastSuccessReport.orders_updated} orders updated</span>
            </div>
          ) : (
            <span className="flex items-center gap-1.5 text-sm text-gray-400">
              <Clock size={15} />
              Never synced — click to fetch all settlement reports
            </span>
          )}
        </div>
      )}

      {/* Recent sync history */}
      {reports.length > 0 && (
        <div className="mt-4 border-t pt-4">
          <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wide">Sync History</p>
          <div className="space-y-1.5">
            {reports.slice(0, 8).map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-500">{new Date(r.created_at).toLocaleString()}</span>
                <div className="flex items-center gap-3">
                  {r.settlement_id && (
                    <span className="text-gray-400 font-mono">{r.settlement_id?.slice(0, 12)}...</span>
                  )}
                  {r.status === 'success' ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle size={11} /> {r.orders_updated} updated
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-500">
                      <AlertCircle size={11} /> failed
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}